<template>
  <div class="searchTonersInks">
    <h3 class="searchTonersInks__title" v-if="title">{{ title }}</h3>
    <form-search-input
      :autocomplete-items="autocompleteItems"
      @click="searchForm"
      @input="fetchAutocomplete"
      @select="selectItem"
      autocomplete
      v-model="searchInput"
    />
    <div class="searchTonersInks__text">
      <span>Lub wybierz z listy</span>
    </div>
    <div class="searchTonersInks__row">
      <!--
        Always render the brand select (options fill in once getBrandList() resolves)
        instead of v-if-ing it in. brandList is fetched client-side in mounted(), so a
        v-if made the select pop in ~a second after paint and shove everything below it
        down (~63px) — a layout shift on the tonery-i-tusze hard load. An empty-options
        multiselect renders at the same height and just shows its placeholder, so the box
        holds its space from first paint and nothing jumps.
      -->
      <form-multiselect
        :options="brandList || []"
        @input="getModelList"
        class="searchTonersInks__select"
        label="Marka"
        placeholder="Wybierz markę"
        required
        searchable
        v-model="searchBrand"
      >
      </form-multiselect>
      <form-multiselect
        :options="modelList"
        @input="selectModel"
        class="searchTonersInks__select"
        key-value="model"
        label="Model"
        option-label="model"
        placeholder="Wybierz model"
        required
        searchable
        v-if="searchBrand && modelList"
        v-model="searchModel"
      >
      </form-multiselect>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import FormSearchInput from '../../components/Form/FormSearchInput';
import FormMultiselect from '../../components/Form/FormMultiselect';

export default {
  name: 'SearchTonersInks',
  components: { FormMultiselect, FormSearchInput },
  props: {
    title: {
      type: String,
      default: '',
    },
    isSearchPage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchInput: '',
      brandList: null,
      modelList: null,
      searchBrand: null,
      searchModel: null,
      autocompleteSource: null,
      autocompleteItems: [],
      autocompleteDevices: null,
      autocompleteEquipment: null,
    };
  },
  computed: {
    ...mapState({
      activeFilters: state => state.productsFilters.search.activeFilters,
      filters: state => state.productsFilters.search.filters,
    }),
  },
  mounted() {
    if (this.$route.query.query) {
      this.searchInput = this.$route.query.query;
      this.fetchAutocomplete(this.searchInput);
    }

    if (this.$route.query.oemCode) {
      this.searchInput = this.$route.query.oemCode;
      this.fetchAutocomplete(this.searchInput);
    }

    if (this.$route.query.device) {
      this.searchInput = this.$route.query.device;
      this.fetchAutocomplete(this.searchInput);
    }

    if (this.$route.query.deviceBrand) {
      this.searchBrand = this.$route.query.deviceBrand;
      this.getModelList();
    }

    if (this.$route.query.deviceModel) {
      this.searchModel = this.$route.query.deviceModel;
    }

    this.getBrandList();
  },
  methods: {
    ...mapActions({
      submitTonersInks: 'productsFilters/submitTonersInks',
    }),

    async getBrandList() {
      const { data } = await this.$axios.get(
        `${this.$config.baseOfixApi}/e-commerce/v1/printing-devices/brands`,
      );
      this.brandList = data.names;
    },
    async getModelList() {
      this.modelList = null;
      const { data } = await this.$axios.get(
        `${this.$config.baseOfixApi}/e-commerce/v1/printing-devices`,
        {
          params: {
            brandName: this.searchBrand,
          },
        },
      );
      this.modelList = data.devices;
    },
    async fetchAutocomplete(val) {
      try {
        if (val === '') {
          this.autocompleteItems = [];
          return;
        }
        if (this.autocompleteSource) {
          this.autocompleteSource.cancel();
        }
        this.autocompleteSource = this.$axios.CancelToken.source();
        const { data } = await this.$axios.get(
          `${this.$config.baseOfixApi}/e-commerce/v1/printing-devices/search/autocomplete`,
          {
            cancelToken: this.autocompleteSource.token,
            params: {
              query: this.searchInput,
            },
          },
        );

        this.autocompleteItems = [];

        if (data.devices && data.devices.length) {
          this.autocompleteItems.push([{ title: 'Urządzenia' }]);
          this.autocompleteItems.push(
            data.devices.map(({ brand, model }) => ({
              data: {
                text: `${brand}, ${model}`,
                device: `${brand}${model}`,
              },
              url: this.localePath({
                name: 'search',
                query: {
                  inkAndTonersOnly: 'true',
                  device: `${brand} ${model}`,
                },
              }),
            })),
          );
        }

        if (data.equipment && data.equipment.length) {
          this.autocompleteItems.push([{ title: 'Artykuły eksploatacyjne' }]);
          this.autocompleteItems.push(
            data.equipment.map(({ brandName, oemCode }) => ({
              data: {
                text: `${brandName}, ${oemCode}`,
                oemCode,
              },
              url: this.localePath({
                name: 'search',
                query: {
                  inkAndTonersOnly: 'true',
                  oemCode,
                },
              }),
            })),
          );
        }
      } catch {}
    },
    selectModel(e) {
      this.$emit('submit');
      if (this.isSearchPage) {
        this.submitTonersInks({
          type: 'search',
          value: {
            inkAndTonersOnly: 'true',
            deviceBrand: this.searchBrand,
            deviceModel: e,
          },
        });
      } else {
        this.$router.push(
          this.localePath({
            name: 'search',
            query: {
              inkAndTonersOnly: 'true',
              deviceBrand: this.searchBrand,
              deviceModel: e,
            },
          }),
        );
      }
    },
    selectItem(e) {
      this.searchInput = e.text;
      this.$emit('submit');
      if (this.searchInput) {
        if (this.isSearchPage) {
          this.submitTonersInks({
            type: 'search',
            value: {
              inkAndTonersOnly: 'true',
              ...(e.device && { device: e.device }),
              ...(e.oemCode && { oemCode: e.oemCode }),
            },
          });
        } else {
          this.$router.push(
            this.localePath({
              name: 'search',
              query: {
                inkAndTonersOnly: 'true',
                ...(e.device && { device: e.device }),
                ...(e.oemCode && { oemCode: e.oemCode }),
              },
            }),
          );
        }
      }
    },
    searchForm() {
      this.$emit('submit');
      if (this.searchInput) {
        if (this.isSearchPage) {
          this.submitTonersInks({
            type: 'search',
            value: {
              inkAndTonersOnly: 'true',
              query: this.searchInput,
            },
          });
        } else {
          this.$router.push(
            this.localePath({
              name: 'search',
              query: {
                inkAndTonersOnly: 'true',
                query: this.searchInput,
              },
            }),
          );
        }
      }
    },
  },
};
</script>

<style lang="scss">
.searchTonersInks {
  &__text {
    text-align: center;
    position: relative;
    margin-top: 3rem;
    font-size: 1.4rem;
    font-weight: 600;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      width: 100%;
      height: 1px;
      background-color: $cAddLight;
    }

    span {
      position: relative;
      z-index: 1;
      background-color: $cWhite;
      padding: 0 1rem;
    }
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    margin-left: -1rem;
    margin-right: -1rem;
    margin-top: 2rem;
  }

  &__select {
    flex: 0 0 50%;
    max-width: 50%;
    padding-left: 1rem;
    padding-right: 1rem;

    @include rwd('small-tablet') {
      flex: 0 0 100%;
      max-width: 100%;
      margin-bottom: 1rem;
    }
  }

  &__title {
    margin-bottom: 1.8rem;
    font-size: 2.4rem;
    line-height: 3rem;
  }
}
</style>
